import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Avatar,
  InputAdornment,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { getColors } from "./bookings.styles";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import dayjs from "dayjs";

const BookingFilters = ({ onApply, onClose, initialFilters = {} }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const primaryColor = theme.palette.primary.main;

  // Date range state
  const [startDate, setStartDate] = useState(
    initialFilters.startDate || dayjs().subtract(30, "day").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    initialFilters.endDate || dayjs().format("YYYY-MM-DD")
  );
  const [datePreset, setDatePreset] = useState(initialFilters.datePreset || "last30days");

  // Service category
  const [serviceCategories, setServiceCategories] = useState(
    initialFilters.serviceCategories || { skin: true, hair: true, dental: true }
  );

  // Consultant
  const [consultant, setConsultant] = useState(initialFilters.consultant || "");

  // Booking status
  const [bookingStatuses, setBookingStatuses] = useState(
    initialFilters.bookingStatuses || {
      scheduled: true,
      confirmed: true,
      ongoing: false,
      completed: false,
      cancelled: false,
    }
  );


  const handleDatePresetClick = (preset) => {
    setDatePreset(preset);
    const now = dayjs();

    switch (preset) {
      case "last7days":
        setStartDate(now.subtract(7, "day").format("YYYY-MM-DD"));
        setEndDate(now.format("YYYY-MM-DD"));
        break;
      case "last30days":
        setStartDate(now.subtract(30, "day").format("YYYY-MM-DD"));
        setEndDate(now.format("YYYY-MM-DD"));
        break;
      default:
        break;
    }
  };

  const handleServiceToggle = (service) => {
    setServiceCategories((prev) => ({
      ...prev,
      [service]: !prev[service],
    }));
  };

  const handleStatusToggle = (status) => {
    setBookingStatuses((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  const handleClearAll = () => {
    setStartDate(dayjs().subtract(30, "day").format("YYYY-MM-DD"));
    setEndDate(dayjs().format("YYYY-MM-DD"));
    setDatePreset("last30days");
    setServiceCategories({ skin: true, hair: true, dental: true });
    setConsultant("");
    setBookingStatuses({
      scheduled: true,
      confirmed: true,
      ongoing: false,
      completed: false,
      cancelled: false,
    });
  };

  const handleApply = () => {
    onApply?.({
      startDate,
      endDate,
      datePreset,
      serviceCategories,
      consultant,
      bookingStatuses,
    });
  };

  const sectionLabelSx = {
    fontSize: 10,
    fontWeight: 700,
    color: colors.textMuted,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    mb: 1.5,
  };

  const getStatusStyle = (statusKey, isSelected) => {
    const baseStyle = {
      height: 32,
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
      borderRadius: 20,
      "& .MuiChip-label": {
        px: 2,
      },
    };

    if (isSelected) {
      return {
        ...baseStyle,
        bgcolor: primaryColor,
        color: "#FFFFFF",
        border: "none",
        "&:hover": {
          bgcolor: primaryColor,
          opacity: 0.9,
        },
      };
    }

    return {
      ...baseStyle,
      bgcolor: "#FFFFFF",
      color: colors.textSecondary,
      border: `1px solid ${colors.border}`,
      "&:hover": {
        bgcolor: colors.bgHover,
        borderColor: primaryColor,
      },
    };
  };

  const consultantOptions = [
    { id: "robert-chen", name: "Robert Chen", avatar: null },
    { id: "elena-rodriguez", name: "Elena Rodriguez", avatar: null },
    { id: "mark-thompson", name: "Mark Thompson", avatar: null },
    { id: "sarah-williams", name: "Sarah Williams", avatar: null },
  ];

  return (
    <Box sx={{ width: 420, p: 3.5, overflow: "visible" }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3.5}>
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 700,
            color: primaryColor,
          }}
        >
          Filter Bookings
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: colors.textMuted,
            "&:hover": {
              bgcolor: colors.bgPrimary,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      {/* Date Range Section */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={sectionLabelSx}>DATE RANGE</Typography>
        <Stack direction="row" spacing={2} mb={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setDatePreset("custom");
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2.5,
                  fontSize: 13,
                  bgcolor: "#fff",
                  height: 48,
                  "& fieldset": {
                    borderColor: colors.border,
                  },
                  "&:hover fieldset": {
                    borderColor: primaryColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                    borderWidth: 1,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  py: 1.5,
                },
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setDatePreset("custom");
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2.5,
                  fontSize: 13,
                  bgcolor: "#fff",
                  height: 48,
                  "& fieldset": {
                    borderColor: colors.border,
                  },
                  "&:hover fieldset": {
                    borderColor: primaryColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                    borderWidth: 1,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  py: 1.5,
                },
              }}
            />
          </Box>
        </Stack>

        {/* Quick Filters */}
        <Stack direction="row" spacing={1.5}>
          <Chip
            label="Last 7 Days"
            size="small"
            onClick={() => handleDatePresetClick("last7days")}
            sx={{
              height: 32,
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 20,
              bgcolor: datePreset === "last7days" ? alpha(primaryColor, 0.15) : "#FFFFFF",
              color: datePreset === "last7days" ? primaryColor : colors.textSecondary,
              border: `1px solid ${datePreset === "last7days" ? primaryColor : colors.border}`,
              cursor: "pointer",
              "& .MuiChip-label": { px: 2 },
              "&:hover": {
                bgcolor: alpha(primaryColor, 0.1),
                borderColor: primaryColor,
              },
            }}
          />
          <Chip
            label="Last 30 Days"
            size="small"
            onClick={() => handleDatePresetClick("last30days")}
            sx={{
              height: 32,
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 20,
              bgcolor: datePreset === "last30days" ? alpha(primaryColor, 0.15) : "#FFFFFF",
              color: datePreset === "last30days" ? primaryColor : colors.textSecondary,
              border: `1px solid ${datePreset === "last30days" ? primaryColor : colors.border}`,
              cursor: "pointer",
              "& .MuiChip-label": { px: 2 },
              "&:hover": {
                bgcolor: alpha(primaryColor, 0.1),
                borderColor: primaryColor,
              },
            }}
          />
        </Stack>
      </Box>

      {/* Service Category Section */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={sectionLabelSx}>SERVICE CATEGORY</Typography>
        <Stack direction="row" spacing={3} alignItems="center">
          {[
            { key: "skin", label: "Skin Care" },
            { key: "hair", label: "Hair Care" },
            { key: "dental", label: "Dental" },
          ].map((service) => (
            <FormControlLabel
              key={service.key}
              control={
                <Checkbox
                  checked={serviceCategories[service.key]}
                  onChange={() => handleServiceToggle(service.key)}
                  sx={{
                    color: colors.border,
                    p: 0.5,
                    "&.Mui-checked": {
                      color: primaryColor,
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 22,
                    },
                  }}
                />
              }
              label={service.label}
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: 14,
                  fontWeight: 500,
                  color: colors.textPrimary,
                  ml: 0.5,
                },
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Consultant Section */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={sectionLabelSx}>CONSULTANT</Typography>
        <FormControl fullWidth size="small">
          <Select
            value={consultant}
            onChange={(e) => setConsultant(e.target.value)}
            displayEmpty
            startAdornment={
              <InputAdornment position="start" sx={{ ml: 1 }}>
                <PersonOutlineIcon sx={{ fontSize: 20, color: colors.textMuted }} />
                {consultant && (
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      ml: 1,
                      fontSize: 12,
                      bgcolor: primaryColor,
                    }}
                  >
                    {consultantOptions.find(c => c.id === consultant)?.name?.charAt(0) || ""}
                  </Avatar>
                )}
              </InputAdornment>
            }
            sx={{
              borderRadius: 2.5,
              fontSize: 14,
              bgcolor: "#fff",
              height: 48,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.border,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: primaryColor,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: primaryColor,
                borderWidth: 1,
              },
              "& .MuiSelect-select": {
                pl: consultant ? 1 : 0,
              },
            }}
          >
            <MenuItem value="">
              <Typography sx={{ fontSize: 14, color: colors.textMuted }}>
                All Consultants
              </Typography>
            </MenuItem>
            {consultantOptions.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: primaryColor }}>
                    {c.name.charAt(0)}
                  </Avatar>
                  <Typography sx={{ fontSize: 14, color: colors.textPrimary }}>
                    {c.name}
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Booking Status Section */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={sectionLabelSx}>BOOKING STATUS</Typography>
        <Stack direction="row" flexWrap="wrap" gap={1.5}>
          {[
            { key: "scheduled", label: "Scheduled" },
            { key: "confirmed", label: "Confirmed" },
            { key: "ongoing", label: "Ongoing" },
            { key: "completed", label: "Completed" },
            { key: "cancelled", label: "Cancelled" },
          ].map((s) => (
            <Chip
              key={s.key}
              label={s.label}
              onClick={() => handleStatusToggle(s.key)}
              sx={getStatusStyle(s.key, bookingStatuses[s.key])}
            />
          ))}
        </Stack>
      </Box>

      {/* Actions */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ borderTop: `1px solid ${colors.borderLight}`, pt: 3, mt: 0, flexWrap: "nowrap" }}
      >
        <Button
          variant="text"
          onClick={handleClearAll}
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: primaryColor,
            textTransform: "none",
            whiteSpace: "nowrap",
            px: 3,
            "&:hover": {
              bgcolor: "transparent",
              color: primaryColor,
              opacity: 0.8,
            },
          }}
        >
          Clear All
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{
            flex: 1,
            fontSize: 14,
            fontWeight: 600,
            bgcolor: primaryColor,
            color: "#fff",
            textTransform: "none",
            whiteSpace: "nowrap",
            borderRadius: 25,
            py: 1.5,
            boxShadow: "none",
            "&:hover": {
              bgcolor: primaryColor,
              opacity: 0.9,
              boxShadow: "none",
            },
          }}
        >
          Apply Filters
        </Button>
      </Stack>
    </Box>
  );
};

export default BookingFilters;
